import * as React from "react";
import { useEffect, useRef } from "react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import { motion, AnimatePresence } from "framer-motion";
import type { Notification } from "../../types/notifications";
import {
    AlertTriangle,
    Bell,
    Eye,
    Home,
    Info,
    ChevronDown,
    ChevronUp,
} from "lucide-react";
import { Button } from "../misc/Button";
import { FaTrash } from "react-icons/fa";
import RichTextViewer from "../misc/rich-text-editor/RichTextViewer";
import { getPlatformIcon } from "../../utils/logo-getter";

interface NotificationItemProps {
    notification: Notification;
    onRead: () => void;
    onRemove: () => void;
    isExpanded: boolean;
    onToggle(): void;
}

const MOBILE_TITLE_LIMIT = 30;

const NotificationItem = ({
    notification,
    onRead,
    onRemove,
    isExpanded,
    onToggle,
}: NotificationItemProps) => {
    const isMobile = useIsMobile(900);
    const navigate = useNavigate();
    const panelRef = useRef<HTMLDivElement>(null);

    const displayTitle = isMobile && notification.title.length > MOBILE_TITLE_LIMIT
        ? `${notification.title.slice(0, MOBILE_TITLE_LIMIT)}...`
        : notification.title;

    // if we auto‐expand, also mark as read on mount
    useEffect(() => {
        if (isExpanded && !notification.isRead) {
            onRead();
        }
    }, [isExpanded, notification.isRead, onRead]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                isExpanded &&
                panelRef.current &&
                !panelRef.current.contains(e.target as Node)
            ) {
                onToggle();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isExpanded, onToggle]);

    const handleToggleExpand = () => {
        if (!isExpanded && !notification.isRead) {
            onRead();
        }
        onToggle();
    };

    const getNotificationIcon = () => {
        switch (notification.type) {
            case "announcement":
                return <Bell className="w-5 h-5 text-[#E9C468]" />;
            case "listingIngested":
                return <Home className="w-5 h-5 text-[#193456]" />;
            case "alert":
                return <AlertTriangle className="w-5 h-5 text-amber-500" />;
            case "captionVideoReady": {
                const platformIcon = getPlatformIcon(notification.payload?.platform ?? "");
                return platformIcon ? (
                    <span className="w-5 h-5 flex items-center justify-center [&>svg]:w-5 [&>svg]:h-5">
                        {platformIcon}
                    </span>
                ) : (
                    <Info className="w-5 h-5 text-[#193456]" />
                );
            }
            default:
                return <Info className="w-5 h-5 text-[#193456]" />;
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, height: 0 }}
            className="relative border-b border-gray-100 last:border-b-0"
            ref={panelRef}
            onMouseDown={(e) => e.stopPropagation()}
        >
            {/* Header */}
            <div
                className={`relative flex items-center p-4 cursor-pointer transition-colors duration-200 ${
                    !notification.isRead ? "bg-[#F0DFAF]/10" : ""
                } hover:bg-gray-50`}
                onClick={handleToggleExpand}
                onMouseDown={(e) => e.stopPropagation()}
            >
                {!notification.isRead && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-[#E9C468] rounded-r-full" />
                )}
                <div className="flex-shrink-0 p-1.5 bg-white rounded-full shadow-sm border border-gray-100 mr-3">
                    {getNotificationIcon()}
                </div>
                <div className="flex-1 min-w-0">
                    <h4
                        className={`text-sm font-medium truncate ${
                            !notification.isRead
                                ? "text-[#193456]"
                                : "text-gray-700"
                        }`}
                    >
                        {displayTitle}
                    </h4>
                    <span className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(notification.createdAt), {
                            addSuffix: true,
                        })}
                    </span>
                </div>
                <div className="ml-2 text-gray-400">
                    {isExpanded ? (
                        <ChevronUp className="w-4 h-4" />
                    ) : (
                        <ChevronDown className="w-4 h-4" />
                    )}
                </div>
            </div>

            {/* Expanded content */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden bg-gray-50 border-t border-gray-100"
                    >
                        <div className="p-4">
                            <RichTextViewer content={notification.message} />
                            <div className="flex items-center justify-between mt-4">
                                {notification.payload?.postId ? (
                                    <Button
                                        variant="outline"
                                        onClick={() => navigate(`/post/${notification.payload!.postId}`)}
                                        className="w-fit"
                                    >
                                        <Eye className="w-4 h-4 mr-1" />
                                        View
                                    </Button>
                                ) : notification.type === "listingsAwaitingReview" ? (
                                    <Button
                                        variant="outline"
                                        onClick={() => navigate("/listings")}
                                        className="w-fit"
                                    >
                                        <Eye className="w-4 h-4 mr-1" />
                                        Review listings
                                    </Button>
                                ) : (
                                    <span />
                                )}
                                <Button
                                    variant="destructive"
                                    onClick={onRemove}
                                    className="w-fit"
                                >
                                    <FaTrash className="w-4 h-4 mr-1" />
                                    Delete
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default NotificationItem;
